"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import { PLATFORMS, SECTIONS, check, defaults, fromIni, toIni, type PalSetting, type Values } from "~/lib/pal-server-settings";

const STORE = "paldeck:server-settings";

// Presets adapted from the MIT-licensed Palworld Dedicated Server Config Creator (BlinkZer0).
const PRESETS: { id: string; label: string; blurb: string; values: Values }[] = [
  { id: "default", label: "Default", blurb: "The game's standard balance.", values: {} },
  {
    id: "casual",
    label: "Casual",
    blurb: "Double XP and catch rate, stronger players, items-only death penalty.",
    values: { ExpRate: 2, PalCaptureRate: 2, PlayerDamageRateAttack: 1.5, PlayerDamageRateDefense: 0.7, PalDamageRateAttack: 1.3, EnemyDropItemRate: 2, CollectionDropRate: 2, WorkSpeedRate: 2, DeathPenalty: "Item", PalEggDefaultHatchingTime: 36 },
  },
  {
    id: "progression",
    label: "Fast progression",
    blurb: "Triple XP, easier catches, more drops, faster eggs.",
    values: { ExpRate: 3, PalCaptureRate: 1.8, EnemyDropItemRate: 1.5, CollectionDropRate: 1.5, WorkSpeedRate: 1.5, PalEggDefaultHatchingTime: 36, DeathPenalty: "Item" },
  },
  {
    id: "builder",
    label: "Builder",
    blurb: "Tougher buildings, slow decay, fast workers, no raids.",
    values: { BuildObjectHpRate: 2, BuildObjectDamageRate: 0.5, BuildObjectDeteriorationDamageRate: 0.1, WorkSpeedRate: 2, CollectionDropRate: 1.5, CollectionObjectHpRate: 0.8, CollectionObjectRespawnSpeedRate: 1.5, bEnableInvaderEnemy: false, DeathPenalty: "Item", PalEggDefaultHatchingTime: 48 },
  },
  {
    id: "lootfest",
    label: "Lootfest",
    blurb: "Triple drops, faster respawns, gear lasts longer.",
    values: { CollectionDropRate: 3, EnemyDropItemRate: 3, CollectionObjectRespawnSpeedRate: 2, EquipmentDurabilityDamageRate: 0.6, ItemCorruptionMultiplier: 0.6, DropItemMaxNum: 5000, DropItemMaxNum_UNKO: 200, DropItemAliveMaxHours: 2 },
  },
  {
    id: "hardcore",
    label: "Hardcore",
    blurb: "Half XP and catches, enemies hit harder, permadeath for you and your Pals.",
    values: { ExpRate: 0.5, PalCaptureRate: 0.5, PlayerDamageRateAttack: 0.8, PlayerDamageRateDefense: 1.5, PalDamageRateAttack: 0.7, EnemyDropItemRate: 0.5, CollectionDropRate: 0.7, WorkSpeedRate: 0.5, DeathPenalty: "All", bHardcore: true, bPalLost: true, bEnableFastTravel: false, PalEggDefaultHatchingTime: 120, PlayerStomachDecreaceRate: 2, PlayerStaminaDecreaceRate: 1.5 },
  },
];

const chip = (on: boolean) =>
  `rounded-md border px-2.5 py-1.5 text-xs transition-colors ${on ? "border-deck-cyan bg-deck-cyan/10 text-deck-text" : "border-deck-line text-deck-muted hover:text-deck-text"}`;

function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)} className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${on ? "bg-deck-cyan" : "bg-deck-highest"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

function Field({ s, value, optional, onChange }: { s: PalSetting; value: Values[string]; optional?: boolean; onChange: (v: Values[string]) => void }) {
  const unset = value === undefined;
  if (s.kind === "bool") {
    if (optional)
      return (
        <div className="flex gap-1.5">
          {(["Default", "On", "Off"] as const).map((o) => {
            const on = o === "Default" ? unset : o === "On" ? value === true : value === false;
            return (
              <button key={o} type="button" className={chip(on)} onClick={() => onChange(o === "Default" ? undefined : o === "On")}>
                {o === "Default" ? "Server default" : o}
              </button>
            );
          })}
        </div>
      );
    return <Toggle on={Boolean(value)} onChange={onChange} label={s.label} />;
  }
  if (s.kind === "enum")
    return (
      <div className="flex flex-wrap gap-1.5">
        {(s.options ?? []).map(([v, l]) => (
          <button key={v} type="button" className={chip(value === v)} onClick={() => onChange(v)}>
            {l}
          </button>
        ))}
      </div>
    );
  if (s.kind === "platforms") {
    const list = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-1.5">
        {PLATFORMS.map((p) => (
          <button key={p} type="button" className={chip(list.includes(p))} onClick={() => onChange(list.includes(p) ? list.filter((x) => x !== p) : [...list, p])}>
            {p}
          </button>
        ))}
      </div>
    );
  }
  if (s.kind === "number") {
    const n = unset ? "" : Number(value);
    const slider = !optional && s.min != null && s.max != null && s.max - s.min <= 1000;
    return (
      <div className="flex items-center gap-3">
        {slider && (
          <input type="range" min={s.min ?? 0} max={s.max ?? 10} step={s.step ?? 1} value={n === "" ? Number(s.default ?? 0) : n} onChange={(e) => onChange(Number(e.target.value))} className="flex-1 accent-[#00F0FF]" aria-label={s.label} />
        )}
        <input
          className={`h-9 rounded-md border border-deck-line bg-deck-bg px-2 text-right font-mono text-sm text-deck-text focus:border-deck-cyan focus:outline-none ${slider ? "w-24" : "w-36"}`}
          inputMode="decimal"
          value={n}
          placeholder={optional ? "default" : undefined}
          onChange={(e) => {
            const t = e.target.value.trim();
            if (t === "" && optional) onChange(undefined);
            else if (/^-?\d*\.?\d*$/.test(t)) onChange(t === "" || t === "-" || t.endsWith(".") ? (t as unknown as number) : Number(t));
          }}
          onBlur={(e) => {
            const t = e.target.value.trim();
            if (t !== "" && Number.isFinite(Number(t))) onChange(Number(t));
          }}
          aria-label={s.label}
        />
      </div>
    );
  }
  return (
    <input
      type={s.kind === "password" ? "password" : "text"}
      className="h-9 w-full rounded-md border border-deck-line bg-deck-bg px-3 text-sm text-deck-text focus:border-deck-cyan focus:outline-none"
      value={unset ? "" : String(value)}
      placeholder={optional ? "default" : undefined}
      autoComplete="off"
      onChange={(e) => onChange(optional && e.target.value === "" ? undefined : e.target.value)}
      aria-label={s.label}
    />
  );
}

export default function Generator() {
  const [values, setValues] = useState<Values>(defaults);
  const [open, setOpen] = useState<Record<string, boolean>>({ "Server Information": true });
  const [q, setQ] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; lines: string[] } | null>(null);
  const [copied, setCopied] = useState(false);
  const [paste, setPaste] = useState("");
  const loaded = useRef(false);
  const base = useMemo(() => defaults(), []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORE);
      if (saved) setValues({ ...defaults(), ...(JSON.parse(saved) as Values) });
    } catch {
      /* storage blocked */
    }
    loaded.current = true;
  }, []);
  useEffect(() => {
    if (!loaded.current) return;
    try {
      localStorage.setItem(STORE, JSON.stringify(values));
    } catch {
      /* ignore */
    }
  }, [values]);

  const ini = useMemo(() => toIni(values), [values]);
  const issues = useMemo(() => check(values), [values]);
  const set = (k: string, v: Values[string]) => setValues((x) => ({ ...x, [k]: v }));
  const search = q.trim().toLowerCase();
  const changed = (s: PalSetting) => JSON.stringify(values[s.key]) !== JSON.stringify(base[s.key]);

  const doImport = (text: string) => {
    try {
      const r = fromIni(text);
      setValues(r.values);
      setMsg({ ok: true, lines: r.notes });
    } catch (e) {
      setMsg({ ok: false, lines: [e instanceof Error ? e.message : String(e)] });
    }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([ini], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "PalWorldSettings.ini";
    a.click();
    URL.revokeObjectURL(url);
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ini);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <div className="grid gap-5 laptop:grid-cols-[1fr_440px]">
      <div className="min-w-0 space-y-4">
        <div className="rounded-xl border border-deck-line bg-deck-surface p-4">
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-deck-cyan">Start from a preset</p>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                title={p.blurb}
                className={chip(false)}
                onClick={() => {
                  setValues((x) => ({ ...defaults(), ServerName: x.ServerName, ServerDescription: x.ServerDescription, ServerPassword: x.ServerPassword, AdminPassword: x.AdminPassword, PublicIP: x.PublicIP, PublicPort: x.PublicPort, ...p.values }));
                  setMsg({ ok: true, lines: [`Applied ${p.label}: ${p.blurb} Your server name, passwords and port were kept.`] });
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label className="cursor-pointer rounded-md bg-deck-raised px-3 py-1.5 text-xs text-deck-text hover:ring-1 hover:ring-deck-cyan">
              Open PalWorldSettings.ini…
              <input
                type="file"
                accept=".ini,text/plain"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (f) doImport(await f.text());
                  e.target.value = "";
                }}
              />
            </label>
            <input value={paste} onChange={(e) => setPaste(e.target.value)} placeholder="…or paste the OptionSettings line" className="h-8 min-w-[200px] flex-1 rounded-md border border-deck-line bg-deck-bg px-2 font-mono text-xs text-deck-text focus:border-deck-cyan focus:outline-none" />
            <button type="button" disabled={!paste.trim()} onClick={() => doImport(paste)} className={chip(false) + " disabled:opacity-40"}>
              Import
            </button>
          </div>
          {msg && (
            <div className={`mt-3 rounded-md border px-3 py-2 text-xs ${msg.ok ? "border-deck-green/40 text-deck-text" : "border-red-400/50 text-red-200"}`}>
              {msg.lines.map((l) => (
                <p key={l}>{l}</p>
              ))}
            </div>
          )}
        </div>

        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a setting (e.g. exp, egg, raid, pvp)…" className="h-10 w-full rounded-lg border border-deck-line bg-deck-surface px-3 text-sm text-deck-text placeholder:text-deck-muted focus:border-deck-cyan focus:outline-none" />

        {SECTIONS.map((sec) => {
          const list = sec.settings.filter((s) => !search || `${s.key} ${s.label} ${s.help}`.toLowerCase().includes(search));
          if (!list.length) return null;
          const isOpen = Boolean(search) || open[sec.title];
          const n = sec.settings.filter(changed).length;
          return (
            <section key={sec.title} className="rounded-xl border border-deck-line bg-deck-surface">
              <button type="button" onClick={() => setOpen((o) => ({ ...o, [sec.title]: !o[sec.title] }))} className="flex w-full items-center justify-between px-4 py-3 text-left" aria-expanded={isOpen}>
                <span className="font-display text-lg font-semibold text-deck-text">
                  {sec.title}
                  {sec.optional && <span className="ml-2 align-middle font-mono text-[10px] uppercase tracking-wider text-deck-muted">optional</span>}
                </span>
                <span className="font-mono text-xs text-deck-muted">
                  {n > 0 && <span className="mr-2 text-deck-cyan">{n} changed</span>}
                  {isOpen ? "−" : "+"}
                </span>
              </button>
              {isOpen && (
                <div className="border-t border-deck-line px-4">
                  {sec.optional && <p className="pt-3 text-xs text-deck-muted">New in Palworld 1.0. These are left out of the file unless you set them, so the server keeps its own defaults.</p>}
                  {list.map((s) => (
                    <div key={s.key} className="grid gap-1 border-b border-deck-line/60 py-3 last:border-0 tablet:grid-cols-[230px_1fr] tablet:items-center tablet:gap-4">
                      <div>
                        <p className="text-sm text-deck-text">
                          {s.label}
                          {changed(s) && <span className="ml-1.5 align-middle font-mono text-[9px] uppercase tracking-wider text-deck-cyan">changed</span>}
                        </p>
                        <p className="font-mono text-[10px] text-deck-muted">{s.key}</p>
                        <p className="text-xs text-deck-muted">{s.help}</p>
                      </div>
                      <Field s={s} value={values[s.key]} optional={sec.optional} onChange={(v) => set(s.key, v)} />
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
        <button type="button" onClick={() => { setValues(defaults()); setMsg({ ok: true, lines: ["Reset every setting to its default."] }); }} className={chip(false)}>
          Reset everything to defaults
        </button>
      </div>

      <aside className="laptop:sticky laptop:top-20 laptop:self-start">
        <div className="rounded-xl border border-deck-line bg-deck-surface">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-deck-line px-4 py-3">
            <p className="font-mono text-xs text-deck-muted">PalWorldSettings.ini</p>
            <div className="flex gap-2">
              <button type="button" onClick={copy} className={chip(false)}>
                {copied ? "Copied" : "Copy"}
              </button>
              <button type="button" onClick={download} className="rounded-md bg-deck-cyan px-3 py-1.5 text-xs font-semibold text-deck-bg">
                Download
              </button>
            </div>
          </div>
          {issues.length > 0 && (
            <ul className="space-y-1 border-b border-deck-line px-4 py-3 text-xs">
              {issues.map((i) => (
                <li key={i.text} className={i.level === "error" ? "text-red-300" : i.level === "warn" ? "text-amber-200" : "text-deck-muted"}>
                  {i.level === "error" ? "✕ " : i.level === "warn" ? "! " : "i "}
                  {i.text}
                </li>
              ))}
            </ul>
          )}
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-all p-4 font-mono text-[11px] leading-relaxed text-deck-text">{ini}</pre>
        </div>
        <p className="mt-2 text-xs text-deck-muted">Your settings are saved in this browser only. Nothing is uploaded.</p>
      </aside>
    </div>
  );
}
